import { ref } from 'vue'
import { filter, fromPairs, get, map } from 'lodash'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import { GetServiceNew } from '@/api/service'
import { GetAttachService } from '@/api/attachService'
import { GetAppointmentServicePairing } from '@/api/appointmentServicePairing'
import { apiFormatAdaptor } from '@/utils/api'
import { appointmentServicePairingServiceTypeConfig } from '@/config/appointmentServicePairing'

/**
 * 服務在選擇表與表單中的 key：服務項目與附加服務放在同一張表，以類型區分
 * @param {{ type: string, id: string }} service 服務
 * @returns {string} `${type}:${id}`
 */
export const toServiceKey = ({ type, id }) => `${type}:${id}`

/**
 * @param {string} key toServiceKey 產生的 key
 * @returns {{ type: string, id: string }} 服務
 */
export const parseServiceKey = (key) => {
  const [type, id] = key.split(':')
  return { type, id }
}

const toServiceOption = (type) => (service) => ({
  id: toServiceKey({ type, id: service.id }),
  type,
  typeName: appointmentServicePairingServiceTypeConfig[type].label,
  name: service.name,
  price: service.price,
  bookingTime: service.bookingTime,
  isPublic: service.isPublic,
})

/**
 * 推薦服務搭配設定的服務選項：本店所有服務項目與附加服務（含下架）
 * 先服務項目、再附加服務，各自依既有排序；附加服務不需要綁定服務項目就能選
 * 一併取得已建立的搭配組合：一個服務只能是一組搭配的主要服務
 */
export const useAppointmentServicePairingServiceOptions = () => {
  const { shopId } = useShop()
  const { fetchAll } = useFetch()
  const serviceOptions = ref([])
  // 主要服務 key → 搭配組合 ID
  const mainServicePairingIds = ref({})
  const loading = ref(false)

  const fetchServiceOptions = async () => {
    loading.value = true
    let services = []
    let attaches = []
    let pairings = []
    await Promise.all([
      fetchAll(GetServiceNew, { shopId: shopId.value }, (res) => { services = res }),
      fetchAll(apiFormatAdaptor(GetAttachService), { shopId: shopId.value }, (res) => { attaches = res }),
      fetchAll(GetAppointmentServicePairing, { shopId: shopId.value }, (res) => { pairings = res }),
    ])
    serviceOptions.value = [
      ...map(services, toServiceOption(appointmentServicePairingServiceTypeConfig.service.value)),
      ...map(attaches, toServiceOption(appointmentServicePairingServiceTypeConfig.serviceAttach.value)),
    ]
    mainServicePairingIds.value = fromPairs(
      map(filter(pairings, 'mainService'), (pairing) => [toServiceKey(pairing.mainService), pairing.id]),
    )
    loading.value = false
  }

  return { serviceOptions, mainServicePairingIds, loading, fetchServiceOptions }
}

/**
 * 預約列表「推薦商品」欄位：依預約的服務項目，顯示推薦服務搭配設定裡搭配的服務名稱
 * 只看服務項目當主要服務的搭配（預約的主服務一定是服務項目）
 */
export const useReservationRecommendedServices = () => {
  const { shopId } = useShop()
  const { fetchAll } = useFetch()
  // 服務項目 ID → 搭配的服務名稱
  const pairedNamesByServiceId = ref({})

  const fetchRecommendedServices = async () => {
    await fetchAll(GetAppointmentServicePairing, { shopId: shopId.value }, (pairings) => {
      const servicePairings = filter(pairings, (pairing) =>
        get(pairing, 'mainService.type') === appointmentServicePairingServiceTypeConfig.service.value)
      pairedNamesByServiceId.value = fromPairs(
        map(servicePairings, (pairing) => [pairing.mainService.id, map(pairing.pairedServices, 'name')]),
      )
    })
  }

  /**
   * @param {string} serviceId 預約的服務項目 ID
   * @returns {string} 搭配的服務名稱，以頓號分隔；沒有設定時為 '-'
   */
  const recommendedServicesText = (serviceId) => {
    const names = pairedNamesByServiceId.value[serviceId]
    return names && names.length ? names.join('、') : '-'
  }

  return { fetchRecommendedServices, recommendedServicesText }
}
